import React, {
  useEffect,
  useState,
  useCallback,
} from "react";
import ProductTable from "../../components/Product/ProductTable";
import api from "../../../utilis/api";
import { server } from "../../../utilis/constant";
import { toast } from "react-toastify";
import ProductForm from "./ProductForm";

const ITEMS_PER_PAGE = 10;

const Product = () => {
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [showCreateProduct, setShowCreateProduct] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    category: "",
    vendor: "",
    tags: "",
  });

  const fetchProducts = async (activeFilters = filters, page = 1) => {
    try {
      // strips empty values before sending
      const params = Object.fromEntries(
        Object.entries(activeFilters).filter(([_, v]) => v.trim() !== ""),
      );
      params.page = page;
      params.limit = ITEMS_PER_PAGE;

      const res = await api.get("/products", { params });
      const payload = res.data.data || {};

      setProducts(payload.products || []);
      setTotalProducts(payload.total || 0);
      setTotalPages(payload.totalPages || 0);
      setCurrentPage(payload.page || page);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const debounce = (fn, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  };

  // inside component
  const debouncedFetch = useCallback(debounce(fetchProducts, 400), []);

  const handleFilterChange = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);
    debouncedFetch(updated, 1); // pass latest state directly — avoids stale closure
  };

  const resetFilters = () => {
    const empty = { search: "", category: "", vendor: "", tags: "" };
    setFilters(empty);
    fetchProducts(empty, 1);
  };

  const handlePageChange = (page) => {
    fetchProducts(filters, page);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");

      // refresh list
      fetchProducts(filters, currentPage);
    } catch (err) {
      toast.error("Delete failed");
      console.log(err);
    }
  };
  return (
    <div className="flex flex-col w-full gap-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#0f0f14] border border-white/10 text-white p-4 rounded-2xl shadow-lg">
        <h1 className="font-semibold text-xl tracking-wide">Manage Products</h1>

        <button
          className="px-4 py-2 bg-green-500 hover:bg-green-600 transition rounded-xl text-sm font-medium"
          onClick={() => setShowCreateProduct(true)}
        >
          + Add Product
        </button>
      </div>

      {/* FILTER BAR */}
      <div className="flex flex-wrap gap-3 p-4 bg-[#0f0f14] border border-white/10 rounded-2xl">
        <input
          type="text"
          placeholder="Search product, SKU..."
          value={filters.search}
          onChange={(e) => handleFilterChange("search", e.target.value)}
          className="px-3 py-2 rounded-xl bg-white/5 text-white text-sm w-56 focus:outline-none focus:ring-2 focus:ring-white/20"
        />

        <select
          value={filters.category}
          onChange={(e) => handleFilterChange("category", e.target.value)}
          className="px-3 py-2 rounded-xl bg-white/5 text-white text-sm focus:outline-none"
        >
          <option value="">All Categories</option>
          <option value="Fragrance">Fragrance</option>
          <option value="Skincare">Skincare</option>
        </select>

        <select
          value={filters.tags}
          onChange={(e) => handleFilterChange("tags", e.target.value)}
          className="px-3 py-2 rounded-xl bg-white/5 text-white text-sm"
        >
          <option value="">All Tags</option>
          <option value="Men">Men</option>
          <option value="Women">Women</option>
          <option value="Unisex">Unisex</option>
          <option value="Best Sales">Best Sales</option>
          <option value="Featured Products">Featured Products</option>
          <option value="New Arrivals">New Arrivals</option>
        </select>

        <button
          onClick={resetFilters}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm transition"
        >
          Reset
        </button>
      </div>

      {/* TABLE WRAPPER */}
      <div className="bg-[#0f0f14] border border-white/10 rounded-2xl p-2 shadow-xl">
        <ProductTable
          data={Array.isArray(products) ? products : []}
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={ITEMS_PER_PAGE}
          totalItems={totalProducts}
          onPageChange={handlePageChange}
          onView={(product) => setSelectedProduct(product)}
          onDelete={handleDelete}
          onEdit={(product) => setEditProduct(product)}
        />
      </div>

      {/* VIEW MODAL */}
      {selectedProduct && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedProduct(null)}
        >
          <div
            className="bg-[#12121a] border border-white/10 text-white w-full max-w-3xl rounded-2xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-white/70 hover:text-white"
              onClick={() => setSelectedProduct(null)}
            >
              ✕
            </button>

            {/* PRODUCT INFO */}
            <div className="flex flex-col sm:flex-row gap-5">
              <img
                src={`${server}/${selectedProduct.image}`}
                className="w-full sm:w-40 h-40 object-cover rounded-xl border border-white/10"
              />

              <div>
                <h2 className="text-xl font-semibold">
                  {selectedProduct.title}
                </h2>

                <p className="text-gray-400 mt-2 text-sm">
                  {selectedProduct.description}
                </p>

                <div className="mt-3 space-y-1 text-sm text-gray-300">
                  <p>
                    <span className="text-white">Category:</span>{" "}
                    {selectedProduct.category}
                  </p>
                  <p>
                    <span className="text-white">Vendor:</span>{" "}
                    {selectedProduct.vendor}
                  </p>
                </div>
              </div>
            </div>

            {/* VARIANTS */}
            <div className="mt-6">
              <h3 className="text-lg font-medium mb-3">Variants</h3>

              {selectedProduct.variants?.length > 0 ? (
                <div className="space-y-2">
                  {selectedProduct.variants.map((v, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between bg-white/5 border border-white/10 p-3 rounded-xl"
                    >
                      <img
                        src={`${server}/${v.image}`}
                        className="w-10 h-10 rounded-md object-cover"
                      />

                      <span className="text-sm">{v.title}</span>
                      <span className="text-sm text-gray-300">
                        Qty: {v.qty}
                      </span>
                      <span className="text-sm font-medium">${v.price}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No variants available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CREATE MODAL */}
      {showCreateProduct && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto"
          onClick={() => setShowCreateProduct(false)}
        >
          <div
            className="w-full max-w-6xl my-4 max-h-[calc(100vh-2rem)] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <ProductForm
              onCancel={() => setShowCreateProduct(false)}
              onSubmit={() => {
                setShowCreateProduct(false);
                fetchProducts(filters, 1);
              }}
            />
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editProduct && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto"
          onClick={() => setEditProduct(null)}
        >
          <div
            className="w-full max-w-6xl my-4 max-h-[calc(100vh-2rem)] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <ProductForm
              initialData={editProduct}
              onCancel={() => setEditProduct(null)}
              onSubmit={() => {
                setEditProduct(null);
                fetchProducts(filters, currentPage);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Product;
